import * as styles from "./ProjectEditorHeader.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Badge } from "@frontend/components/atoms/Badge";
import type { BadgeVariant } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Heading } from "@frontend/components/atoms/Heading";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { TruncateMiddle } from "@frontend/components/atoms/TruncateMiddle";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderHeading,
  PageHeaderMeta,
  type PageHeaderVariant,
  PageHeaderVisual,
} from "@frontend/components/organisms/PageHeader";
import { url } from "@frontend/lib/http";
import { useLingui } from "@lingui/react/macro";

import { projectEditorAtom } from "../stores/editor";
import type { ProjectType } from "../types";
import { openHoursValidation } from "../utils/windows";

export type ProjectEditorHeaderProps = {
  /** What an empty form opens as, until the loaded project says it itself. */
  type: ProjectType;
  variant?: PageHeaderVariant;
};

/**
 * What `Project/page_title_card.html.twig` shows above the form: the project,
 * where it stands, and the two records it hangs from.
 */
const TONES: Record<string, BadgeVariant> = {
  inloop: "info",
  billed: "warning",
  archived: "neutral",
};

export function ProjectEditorHeader({
  type,
  variant = "card",
}: ProjectEditorHeaderProps): JSX.Element {
  const { t } = useLingui();

  const { project } = useAtomValue(projectEditorAtom);

  // A loaded project says its type for itself; an empty form opens as one.
  const shownType = project?.type ?? type;
  const intervention = project?.intervention;

  // Named rather than read inline, so lingui keeps a readable placeholder.
  const createdBy = project?.createdBy;
  const createdAt = project?.createdAt;

  // Read here rather than in a helper: a lingui `t` handed to another function
  // renders empty strings, without saying so.
  const emptyTitle =
    "subscription" === shownType ? t`New subscription` : t`New intervention`;

  // The client fronts the record as the employee does on an absence: the
  // photo, or two letters of the name. Only a form with no client yet shows
  // the type icon. The visual is `plain` whatever the header: a round avatar
  // wants no box around it.
  const clientLabel = project?.clientLabel ?? null;

  return (
    <PageHeader variant={variant}>
      <PageHeaderVisual variant="plain">
        {/* The square sizes the avatar, never the reverse, so a dialog header
            keeps its height. Inline, so it wins over the recipe whatever the
            order the stylesheets land in. */}
        <Avatar
          size="md"
          style={{ width: "100%", height: "100%" }}
          url={project?.clientAvatarUrl ?? undefined}
          fallback={
            clientLabel ? (
              initials(clientLabel)
            ) : (
              <Icon name={"intervention" === shownType ? "Zap" : "Network"} />
            )
          }
        />
      </PageHeaderVisual>

      <PageHeaderContent>
        <PageHeaderHeading>
          <Heading name={project?.label ?? emptyTitle} suffix={project?.id} />

          {project?.statusLabel ? (
            <Badge variant={TONES[project.status ?? ""] ?? "success"}>
              {project.statusLabel}
            </Badge>
          ) : null}
        </PageHeaderHeading>

        {project && (project.clientId || intervention?.subscriptionId) ? (
          <PageHeaderMeta>
            {project.clientId ? (
              <Heading
                name={project.clientLabel ?? ""}
                suffix={project.clientId}
                href={url("azuris_client_index", { path: project.clientId })}
              />
            ) : null}

            {intervention?.subscriptionId ? (
              <Heading
                name={intervention.subscriptionLabel ?? ""}
                suffix={intervention.subscriptionId}
                href={url("eco_pro_project_subscription_index", {
                  path: intervention.subscriptionId,
                })}
              />
            ) : null}

            {createdBy && createdAt ? (
              <TruncateMiddle
                className={styles.created}
                text={t`Created by ${createdBy} on ${createdAt}`}
              />
            ) : null}
          </PageHeaderMeta>
        ) : null}
      </PageHeaderContent>

      {project?.canValidateHours ? (
        <PageHeaderActions>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="neutral"
                  aria-label={t`Hours validation`}
                  onClick={() =>
                    openHoursValidation(project.id, t`Hours validation`)
                  }
                >
                  <Icon name="Clock" size="sm" />
                </Button>
              }
            />

            <TooltipContent side="bottom">{t`Hours validation`}</TooltipContent>
          </Tooltip>
        </PageHeaderActions>
      ) : null}
    </PageHeader>
  );
}

/** Two letters for the avatar: the first of the first two words. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");
}
